// Gemini wrapper — calls our Supabase Edge Function (`gemini-proxy`) so the
// real Gemini API key never enters the browser bundle.
//
// FALLBACK: until the user deploys the Edge Function, this file falls back
// to the legacy in-browser SDK call so the app keeps working. Once
// `gemini-proxy` is deployed and `REACT_APP_GEMINI_PROXY_ENABLED=true` is
// set, the proxy path is used exclusively.
import { GoogleGenerativeAI } from "@google/generative-ai";
import { supabase } from "./supabase";

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const PROXY_ENABLED = process.env.REACT_APP_GEMINI_PROXY_ENABLED === "true";
const LEGACY_KEY = process.env.REACT_APP_GEMINI_API_KEY;
const LEGACY_GEN = LEGACY_KEY ? new GoogleGenerativeAI(LEGACY_KEY) : null;

export const MODEL = process.env.REACT_APP_GEMINI_MODEL || "gemini-2.5-flash";

async function callProxy({ prompt, mode = "general", json = false }) {
  const { data: sess } = await supabase.auth.getSession();
  const token = sess?.session?.access_token;
  if (!token) throw new Error("not authenticated");
  const res = await fetch(`${SUPABASE_URL}/functions/v1/gemini-proxy`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ prompt, mode, json }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    if (res.status === 429) throw new Error("Rate limit reached. Please try again later.");
    if (res.status === 403) throw new Error("Premium required.");
    if (res.status === 401) throw new Error("Please sign in again.");
    throw new Error(err.error || `Gemini proxy error (${res.status})`);
  }
  return (await res.json()).text;
}

async function callLegacy({ prompt, json: wantJson }) {
  if (!LEGACY_GEN) throw new Error("Gemini API key missing");
  const model = LEGACY_GEN.getGenerativeModel({
    model: MODEL,
    generationConfig: wantJson ? { responseMimeType: "application/json" } : {},
  });
  // Retry once on transient 503.
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      const res = await model.generateContent(prompt);
      return res.response.text();
    } catch (err) {
      const msg = String(err?.message || "");
      const overloaded = msg.includes("503") || /overload/i.test(msg);
      if (!overloaded || attempt === 2) throw err;
      await new Promise((r) => setTimeout(r, 800 * (attempt + 1)));
    }
  }
}

function safeJsonParse(text) {
  let t = text.replace(/^```json\s*/i, "").replace(/```\s*$/i, "").trim();
  try {
    return JSON.parse(t);
  } catch {
    const start = Math.min(
      ...["[", "{"].map((c) => {
        const i = t.indexOf(c);
        return i === -1 ? Infinity : i;
      }),
    );
    if (start !== Infinity) return JSON.parse(t.slice(start));
    throw new Error("Could not parse Gemini JSON");
  }
}

/**
 * Call Gemini and parse JSON output safely.
 * @param {string} prompt
 * @param {{ mode?: "dupe_finder" | "glow_report" | "general" }} opts
 */
export async function geminiJSON(prompt, opts = {}) {
  const mode = opts.mode || "general";
  const text = PROXY_ENABLED
    ? await callProxy({ prompt, mode, json: true })
    : await callLegacy({ prompt, json: true });
  return safeJsonParse(text);
}

/**
 * Plain text completion from Gemini.
 */
export async function geminiText(prompt, opts = {}) {
  const mode = opts.mode || "general";
  return PROXY_ENABLED
    ? callProxy({ prompt, mode, json: false })
    : callLegacy({ prompt, json: false });
}

/**
 * End-to-end Dupe Finder via the Edge Function.
 * The function retrieves DB candidates + calls Gemini + caches the result.
 */
export async function dupeSearch({ query, tier, profile }) {
  if (!PROXY_ENABLED) {
    throw new Error("Dupe Finder requires the server-side proxy (set REACT_APP_GEMINI_PROXY_ENABLED=true).");
  }
  const { data: sess } = await supabase.auth.getSession();
  const token = sess?.session?.access_token;
  if (!token) throw new Error("not authenticated");
  const res = await fetch(`${SUPABASE_URL}/functions/v1/gemini-proxy`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ mode: "dupe_search", query, tier, profile }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    if (res.status === 429) {
      const mins = err.retryAfter ? Math.ceil(err.retryAfter / 60) : null;
      throw new Error(
        mins
          ? `You've reached the search limit. Try again in ~${mins} minute${mins === 1 ? "" : "s"}.`
          : "Rate limit reached. Please try again later."
      );
    }
    if (res.status === 403) throw new Error("Premium required to use the Dupe Finder.");
    if (res.status === 401) throw new Error("Please sign in again.");
    if (res.status === 502) {
      throw new Error(err.detail || "Our matching service is busy right now. Please try again in a moment.");
    }
    throw new Error(err.detail || err.error || `Dupe search failed (${res.status})`);
  }
  return res.json(); // { dupes: [...], fromCache: boolean }
}
